import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  Select,
  Badge,
  useToast,
  Spinner,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardHeader,
  CardBody,
  Heading,
  IconButton,
  Flex,
  Tag,
  TagLabel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  SimpleGrid,
  Code,
} from '@chakra-ui/react';
import {
  LinkIcon,
  DeleteIcon,
  RepeatIcon,
  CheckIcon,
  AttachmentIcon,
} from '@chakra-ui/icons';

const API_HEADERS = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const ApiIntegrationTab = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [sourceType, setSourceType] = useState('text');
  const [documentationText, setDocumentationText] = useState('');
  const [documentationUrl, setDocumentationUrl] = useState('');
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  // Auth config modal
  const { isOpen: isAuthOpen, onOpen: onAuthOpen, onClose: onAuthClose } = useDisclosure();
  const [authType, setAuthType] = useState('none');
  const [authToken, setAuthToken] = useState('');
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authApiKey, setAuthApiKey] = useState('');
  const [authApiHeader, setAuthApiHeader] = useState('X-API-Key');
  const [editBaseUrl, setEditBaseUrl] = useState('');

  const toast = useToast();

  const fetchIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/integrations', { headers: API_HEADERS() });
      if (!response.ok) throw new Error('Erro ao carregar integrações');
      const data = await response.json();
      setIntegrations(data.integrations || []);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const handleProcess = async () => {
    if (!geminiApiKey) {
      toast({ title: 'Erro', description: 'Insira a chave da API do Google Gemini', status: 'warning', duration: 3000 });
      return;
    }

    if (sourceType === 'text' && (!documentationText || documentationText.trim().length < 20)) {
      toast({ title: 'Erro', description: 'Insira a documentação da API (mínimo 20 caracteres)', status: 'warning', duration: 3000 });
      return;
    }

    if (sourceType === 'link' && !documentationUrl) {
      toast({ title: 'Erro', description: 'Insira a URL da documentação', status: 'warning', duration: 3000 });
      return;
    }

    if (sourceType === 'pdf' && !pdfFile) {
      toast({ title: 'Erro', description: 'Selecione um arquivo PDF', status: 'warning', duration: 3000 });
      return;
    }

    setProcessing(true);

    try {
      let response;

      if (sourceType === 'pdf') {
        const formData = new FormData();
        formData.append('source_type', 'pdf');
        formData.append('gemini_api_key', geminiApiKey);
        formData.append('pdf_file', pdfFile);

        response = await fetch('/api/integrations/process', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });
      } else {
        response = await fetch('/api/integrations/process', {
          method: 'POST',
          headers: API_HEADERS(),
          body: JSON.stringify({
            source_type: sourceType,
            documentation_text: sourceType === 'text' ? documentationText : undefined,
            documentation_url: sourceType === 'link' ? documentationUrl : undefined,
            gemini_api_key: geminiApiKey
          })
        });
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao processar documentação');
      }

      toast({
        title: 'Integração criada!',
        description: `${data.integration.name} - ${data.integration.routes?.routes?.length || 0} rotas identificadas`,
        status: 'success',
        duration: 5000
      });

      // Limpar formulário
      setDocumentationText('');
      setDocumentationUrl('');
      setPdfFile(null);

      fetchIntegrations();
    } catch (error) {
      toast({ title: 'Erro ao processar', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setProcessing(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await fetch(`/api/integrations/${id}/toggle`, {
        method: 'PUT',
        headers: API_HEADERS()
      });
      if (!response.ok) throw new Error('Erro ao alterar status');
      fetchIntegrations();
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000 });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta integração?')) return;

    try {
      const response = await fetch(`/api/integrations/${id}`, {
        method: 'DELETE',
        headers: API_HEADERS()
      });
      if (!response.ok) throw new Error('Erro ao excluir');
      toast({ title: 'Integração excluída', status: 'success', duration: 3000 });
      fetchIntegrations();
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000 });
    }
  };

  const handleReprocess = async (id) => {
    try {
      const response = await fetch(`/api/integrations/${id}/reprocess`, {
        method: 'POST',
        headers: API_HEADERS()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      toast({ title: 'Reprocessado com sucesso', status: 'success', duration: 3000 });
      fetchIntegrations();
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000 });
    }
  };

  const openAuthConfig = (integration) => {
    setSelectedIntegration(integration);
    setAuthType(integration.auth_type || 'none');
    setEditBaseUrl(integration.base_url || '');
    // Reset auth fields
    setAuthToken('');
    setAuthUsername('');
    setAuthPassword('');
    setAuthApiKey('');
    setAuthApiHeader('X-API-Key');
    onAuthOpen();
  };

  const handleSaveAuth = async () => {
    if (!selectedIntegration) return;

    let auth_config = {};
    if (authType === 'bearer') {
      auth_config = { token: authToken };
    } else if (authType === 'basic') {
      auth_config = { username: authUsername, password: authPassword };
    } else if (authType === 'api_key') {
      auth_config = { key: authApiKey, header: authApiHeader };
    }

    try {
      const response = await fetch(`/api/integrations/${selectedIntegration.id}/auth`, {
        method: 'PUT',
        headers: API_HEADERS(),
        body: JSON.stringify({
          auth_type: authType,
          auth_config,
          base_url: editBaseUrl || undefined
        })
      });
      if (!response.ok) throw new Error('Erro ao salvar');
      toast({ title: 'Configuração salva', status: 'success', duration: 3000 });
      onAuthClose();
      fetchIntegrations();
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000 });
    }
  };

  const parseRoutes = (routesStr) => {
    try {
      const parsed = typeof routesStr === 'string' ? JSON.parse(routesStr) : routesStr;
      return parsed.routes || parsed || [];
    } catch {
      return [];
    }
  };

  const getMethodColor = (method) => {
    const colors = { GET: 'green', POST: 'blue', PUT: 'orange', DELETE: 'red', PATCH: 'purple' };
    return colors[method?.toUpperCase()] || 'gray';
  };

  const getPurposeLabel = (purpose) => {
    const labels = {
      register_infraction: '📝 Registrar Infração',
      query_infraction: '🔍 Consultar Infração',
      query_vehicle: '🚗 Consultar Veículo',
      register_vehicle: '🚙 Registrar Veículo',
      authenticate: '🔐 Autenticação',
      other: '📋 Outro'
    };
    return labels[purpose] || purpose;
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Formulário de nova integração */}
      <Card>
        <CardHeader>
          <Heading size="md">Integrar API de Infrações</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Forneça a documentação da API externa e a IA fará a integração automaticamente
          </Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Como funciona?</AlertTitle>
                <AlertDescription fontSize="xs">
                  1. Forneça a documentação da API via <strong>Link</strong>, <strong>PDF</strong> ou <strong>Texto colado</strong><br />
                  2. A IA (Google Gemini) analisará e extrairá as rotas automaticamente<br />
                  3. As rotas ficarão salvas no banco de dados<br />
                  4. Na tela de Batches, ao confirmar infrações, você poderá enviar para a API integrada
                </AlertDescription>
              </Box>
            </Alert>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Chave da API do Google Gemini</FormLabel>
              <Input
                type="password"
                placeholder="Insira sua API Key do Google Gemini"
                value={geminiApiKey}
                onChange={(e) => setGeminiApiKey(e.target.value)}
                size="sm"
              />
              <FormHelperText fontSize="xs">
                Obtenha em: <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" style={{ color: '#3182CE' }}>aistudio.google.com/apikey</a>
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Tipo de documentação</FormLabel>
              <Select value={sourceType} onChange={(e) => setSourceType(e.target.value)} size="sm">
                <option value="text">Texto (colar documentação)</option>
                <option value="link">Link (URL da documentação)</option>
                <option value="pdf">PDF (upload de arquivo)</option>
              </Select>
              <FormHelperText fontSize="xs" color="orange.500">
                Apenas estes 3 formatos são suportados: Link, PDF ou Texto colado
              </FormHelperText>
            </FormControl>

            {sourceType === 'text' && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Documentação da API</FormLabel>
                <Textarea
                  placeholder={"Cole aqui toda a documentação da API...\n\nExemplo:\nPOST /api/v1/infractions\nBody: { plate: string, code: number, date: string }\nResponse: { id: number, status: string }\n\nGET /api/v1/vehicles/{plate}\nResponse: { plate: string, owner: string, model: string }"}
                  value={documentationText}
                  onChange={(e) => setDocumentationText(e.target.value)}
                  minH="200px"
                  size="sm"
                  fontFamily="mono"
                  fontSize="xs"
                />
                <FormHelperText fontSize="xs">
                  Inclua: endpoints, métodos HTTP, parâmetros, exemplos de request/response, autenticação
                </FormHelperText>
              </FormControl>
            )}

            {sourceType === 'link' && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">URL da Documentação</FormLabel>
                <InputGroup size="sm">
                  <InputLeftElement>
                    <LinkIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="https://api.exemplo.com/docs ou swagger.json"
                    value={documentationUrl}
                    onChange={(e) => setDocumentationUrl(e.target.value)}
                  />
                </InputGroup>
                <FormHelperText fontSize="xs">
                  Suporta páginas HTML, JSON (Swagger/OpenAPI), documentação em texto
                </FormHelperText>
              </FormControl>
            )}

            {sourceType === 'pdf' && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Arquivo PDF</FormLabel>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                  size="sm"
                  p={1}
                />
                {pdfFile && (
                  <Tag mt={2} colorScheme="blue" size="sm">
                    <AttachmentIcon mr={1} />
                    <TagLabel>{pdfFile.name}</TagLabel>
                  </Tag>
                )}
              </FormControl>
            )}

            <Button
              colorScheme="teal"
              onClick={handleProcess}
              isLoading={processing}
              loadingText="Processando com IA..."
              leftIcon={<CheckIcon />}
              isDisabled={!geminiApiKey}
            >
              Processar Documentação e Criar Integração
            </Button>
          </VStack>
        </CardBody>
      </Card>

      <Divider />

      {/* Lista de integrações existentes */}
      <Box>
        <HStack justify="space-between" mb={4}>
          <Heading size="md">Integrações Configuradas</Heading>
          <IconButton
            icon={<RepeatIcon />}
            size="sm"
            onClick={fetchIntegrations}
            aria-label="Recarregar"
          />
        </HStack>

        {loading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" />
          </Flex>
        ) : integrations.length === 0 ? (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">Nenhuma integração configurada. Use o formulário acima para criar uma.</Text>
          </Alert>
        ) : (
          <VStack spacing={4} align="stretch">
            {integrations.map((integration) => {
              const routes = parseRoutes(integration.routes);
              const hasRegisterRoute = routes.some(r => r.purpose === 'register_infraction');

              return (
                <Card key={integration.id} variant="outline">
                  <CardHeader pb={2}>
                    <Flex justify="space-between" align="center">
                      <HStack>
                        <Heading size="sm">{integration.name}</Heading>
                        <Badge colorScheme={integration.is_active ? 'green' : 'gray'}>
                          {integration.is_active ? 'Ativa' : 'Inativa'}
                        </Badge>
                        {hasRegisterRoute && (
                          <Badge colorScheme="purple">Envio de Infrações</Badge>
                        )}
                        <Badge colorScheme="blue">{integration.source_type?.toUpperCase()}</Badge>
                      </HStack>
                      <HStack>
                        <Switch
                          isChecked={integration.is_active}
                          onChange={() => handleToggle(integration.id)}
                          size="sm"
                        />
                        <IconButton
                          icon={<RepeatIcon />}
                          size="xs"
                          onClick={() => handleReprocess(integration.id)}
                          aria-label="Reprocessar"
                          title="Reprocessar documentação com IA"
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size="xs"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDelete(integration.id)}
                          aria-label="Excluir"
                        />
                      </HStack>
                    </Flex>
                    {integration.description && (
                      <Text fontSize="xs" color="gray.500" mt={1}>{integration.description}</Text>
                    )}
                  </CardHeader>

                  <CardBody pt={0}>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                      <Box>
                        <Text fontSize="xs" color="gray.500">URL Base</Text>
                        <Code fontSize="xs">{integration.base_url || 'Não definida'}</Code>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">Autenticação</Text>
                        <Text fontSize="sm">{integration.auth_type || 'Nenhuma'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">Rotas Identificadas</Text>
                        <Text fontSize="sm" fontWeight="bold">{routes.length}</Text>
                      </Box>
                    </SimpleGrid>

                    <Button size="xs" colorScheme="blue" variant="outline" onClick={() => openAuthConfig(integration)} mb={3}>
                      Configurar Autenticação / URL Base
                    </Button>

                    <Accordion allowToggle>
                      <AccordionItem border="none">
                        <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                          <Text fontSize="sm" fontWeight="medium" flex={1} textAlign="left">
                            📋 Ver Rotas ({routes.length})
                          </Text>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel px={0}>
                          <VStack spacing={2} align="stretch">
                            {routes.map((route, idx) => (
                              <Box key={idx} p={2} bg="gray.50" borderRadius="md" borderLeft="3px solid" borderLeftColor={`${getMethodColor(route.method)}.400`}>
                                <HStack mb={1}>
                                  <Badge colorScheme={getMethodColor(route.method)} fontSize="xs">
                                    {route.method}
                                  </Badge>
                                  <Code fontSize="xs">{route.path}</Code>
                                  <Tag size="sm" colorScheme="teal">
                                    <TagLabel fontSize="xs">{getPurposeLabel(route.purpose)}</TagLabel>
                                  </Tag>
                                </HStack>
                                <Text fontSize="xs" color="gray.600">{route.description}</Text>
                                {route.request_body && Object.keys(route.request_body).length > 0 && (
                                  <Box mt={1}>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.500">Body:</Text>
                                    <Code fontSize="xs" display="block" whiteSpace="pre-wrap" p={1}>
                                      {JSON.stringify(route.request_body, null, 2)}
                                    </Code>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </CardBody>
                </Card>
              );
            })}
          </VStack>
        )}
      </Box>

      {/* Modal de configuração de autenticação */}
      <Modal isOpen={isAuthOpen} onClose={onAuthClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Configurar Autenticação</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">URL Base</FormLabel>
                <Input
                  value={editBaseUrl}
                  onChange={(e) => setEditBaseUrl(e.target.value)}
                  placeholder="https://api.exemplo.com/v1"
                  size="sm"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Tipo de Autenticação</FormLabel>
                <Select value={authType} onChange={(e) => setAuthType(e.target.value)} size="sm">
                  <option value="none">Sem autenticação</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="api_key">API Key</option>
                  <option value="basic">Basic Auth</option>
                </Select>
              </FormControl>

              {authType === 'bearer' && (
                <FormControl>
                  <FormLabel fontSize="sm">Token</FormLabel>
                  <Input
                    type="password"
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    placeholder="Bearer token"
                    size="sm"
                  />
                </FormControl>
              )}

              {authType === 'api_key' && (
                <>
                  <FormControl>
                    <FormLabel fontSize="sm">Header Name</FormLabel>
                    <Input
                      value={authApiHeader}
                      onChange={(e) => setAuthApiHeader(e.target.value)}
                      placeholder="X-API-Key"
                      size="sm"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">API Key</FormLabel>
                    <Input
                      type="password"
                      value={authApiKey}
                      onChange={(e) => setAuthApiKey(e.target.value)}
                      placeholder="Sua API key"
                      size="sm"
                    />
                  </FormControl>
                </>
              )}

              {authType === 'basic' && (
                <>
                  <FormControl>
                    <FormLabel fontSize="sm">Usuário</FormLabel>
                    <Input
                      value={authUsername}
                      onChange={(e) => setAuthUsername(e.target.value)}
                      placeholder="username"
                      size="sm"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Senha</FormLabel>
                    <Input
                      type="password"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      placeholder="password"
                      size="sm"
                    />
                  </FormControl>
                </>
              )}

              <Button colorScheme="teal" onClick={handleSaveAuth} width="full" size="sm">
                Salvar Configuração
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ApiIntegrationTab;
